import { useState, useEffect } from "react";
import { Routes, Route, useNavigate, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useAuthorizationCheck } from "./hooks/useAuthorizationCheck";
import { useVersionCheck } from "hooks/useVersionCheck";
import { refreshToken } from "service/userservice/refreshToken";
import { useContext } from "react";
import { AgentData } from "context/Agent/DataProvider";
import { getAdminAlerts } from "redux/slices/getAdminAlertsSlice";
import { convertToTitleCase } from "js/formatting";
import {
  screenScrollTo,
  isCSR,
  isCSRAdmin,
  isFlagEnabled,
} from "./js/utilities";

import config from "config/config";

import Countdown from "react-countdown";
import GeneralPurposeDialog from "components/General/GeneralPurposeDialog";
import { useTranslation } from "react-i18next";

import SVGSprite from "components/General/SvgSprite";
import Header from "components/AgentPortal/General/Header";
import CsrSearchboard from "components/AgentPortal/General/CSRSearchboard";
import UnauthHeader from "components/AgentPortal/General/UnauthHeader";
import AgentBreadcrumb from "components/AgentPortal/General/AgentBreadcrumb";
import AuthenticatedUserChat from "components/Chat/AuthenticatedUserChat";
import UnauthenticatedUserChat from "components/Chat/UnauthenticatedUserChat";
import Footer from "components/General/Footer";
import AgentSessionRefresherDialog from "components/AgentPortal/ModalDialogs/AgentSessionRefresherModal";
import Toast from "components/General/Toast";

import Landing from "routes/AgentPortal/Landing/index";
import Register from "routes/AgentPortal/register";
import ForgotPasswordContainer from "routes/forgot-password";
import ForgotUsernameContainer from "routes/forgot-username";
import ResetPasswordContainer from "routes/reset-password";

import Dashboard from "routes/AgentPortal/Dashboard";
import BookOfBusiness from "routes/AgentPortal/book-of-business";
import PolicyLookUp from "routes/AgentPortal/policyLookUp";
import PolicyDetails from "routes/AgentPortal/PolicyDetails";

import ServicesClaims from "routes/AgentPortal/ServicesAndClaims";
import FileAClaim from "routes/AgentPortal/file-a-claim";
import Claims from "routes/claims";
import ClaimsStatus from "routes/claims-status";
import Forms from "routes/AgentPortal/Forms";

import Commissions from "routes/AgentPortal/Commissions";

import Messages from "routes/AgentPortal/Messages";

import Help from "routes/AgentPortal/Help";
import HelpFaqs from "routes/help-faqs";

import Profile from "routes/AgentPortal/profile";
import MakeAPayment from "routes/AgentPortal/MakeAPayment";
import AssistantDetails from "routes/AgentPortal/assistant-details";
import AccessDenied from "routes/access-denied";

import CSRAdmin from "routes/CSRAdmin";

import TermsOfUse from "routes/terms-of-use";
import PrivacyPolicy from "routes/privacy-policy";
import CaliforniaConsumerPrivacyStatement from "routes/california-consumer-privacy-statement";
import Services from "routes/AgentPortal/Services";
import AgentClaims from "routes/AgentPortal/ServicesAndClaims/agent-claims";
import { ChatContext } from "context/Agent/ChatProvider";
import CSRFormsPanel from "routes/Forms/CSRFormsPanel";

export default function App() {
  useVersionCheck();
  const { user } = useSelector((s) => s.user);
  const { csrUser } = useSelector((s) => s.csrUser);
  const { t } = useTranslation();
  let navigate = useNavigate();
  let location = useLocation();
  const dispatch = useDispatch();
  const { agentPolicies, agentClaims } = useContext(AgentData);
  const {
    hideCustomChat,
    setHideCustomChat,
    connectingToSF,
    setConnectingToSF,
    openChatWindow,
    setOpenChatWindow,
  } = useContext(ChatContext);
  const [currentCounterTime, setCurrentCounterTime] = useState(null);
  const [countdownIdentifier, setCountdownIdentifier] = useState(0);
  const [sessionDialogState, setSessionDialogState] = useState(false);
  const [tokenErrorDialog, setTokenErrorDialog] = useState(false);
  const [bannerMessage, setBannerMessage] = useState();
  const [toastMessage, setToastMessage] = useState();
  const [bannerTitle, setBannerTitle] = useState();
  const [toasTitle, setToastTitle] = useState();

  useEffect(() => {
    screenScrollTo();

    if (
      !user?.userId &&
      !isBasePathName() &&
      location.pathname.split("/")[1] !== "help"
    ) {
      if (isCSR(csrUser) || isCSRAdmin(csrUser)) {
        if (
          !isFlagEnabled("ENABLE_CSR_ADMIN") &&
          !location.pathname === "/csradmin" &&
          !isCSRAdmin(csrUser)
        )
          navigate("/dashboard", { replace: true });
      } else {
        navigate("/", { replace: true });
      }
    }

    if (!isBasePathName() && location.pathname.split("/")[1] !== "help") {
      // Refersh Token
      refershToken();
    }

    if (user?.userId || isCSR(csrUser) || isCSRAdmin(csrUser)) {
      //set timer countdown to 1 hour and 55 mints
      setCurrentCounterTime(
        Date.now() + parseInt(config.SESSION_REFRESH_TIMEOUT)
      );
      setCountdownIdentifier(countdownIdentifier + 1);
    }
  }, [location.pathname]);

  const isBasePathName = () => {
    return [
      "/",
      "/forgot-password",
      "/forgot-username",
      "/register",
      "/reset-password",
      "/terms-of-use",
      "/privacy-policy",
      "/california-consumer-privacy-statement",
      "/survey",
    ].includes(location.pathname);
  };

  useEffect(() => {
    // if user or csr user not loggedin redirect to login
    // Refershing token throw 401 redirect to login
    if (
      !user?.userId &&
      !csrUser?.userId &&
      !isBasePathName() &&
      location.pathname.split("/")[1] !== "help"
    )
      navigate("/", { replace: true });
    dispatch(getAdminAlerts()).then((response) => {
      if (response?.meta?.requestStatus === "fulfilled") {
        let agentToast = response?.payload?.activeAlerts?.filter(
          (a) =>
            (a.portal === "agent" || a.portal === "both") && a.group === "toast"
        );
        let agentBanner = response?.payload?.activeAlerts?.filter(
          (a) =>
            (a.portal === "agent" || a.portal === "both") &&
            a.group === "banner"
        );
        let agentSortedBanner = agentBanner?.sort((a, b) =>
          a.id > b.id ? -1 : 1
        )[0];

        let agentSortedToast = agentToast?.sort((a, b) =>
          a.id > b.id ? -1 : 1
        )[0];
        setBannerMessage(agentSortedBanner?.message);
        setBannerTitle(convertToTitleCase(agentSortedBanner?.title));
        setToastMessage(agentSortedToast?.message);
        setToastTitle(convertToTitleCase(agentSortedToast?.title));
      }
    });
  }, [user, csrUser, location.pathname]);

  const refershToken = async () => {
    // Refershing token
    await refreshToken()
      .then((response) => {
        return response;
      })
      .catch((error) => {
        console.log(error);
        return false;
      });
  };

  const extendSession = () => {
    // Refersh Token
    refershToken();
    setSessionDialogState(false);
    setCurrentCounterTime(
      Date.now() + parseInt(config.SESSION_REFRESH_TIMEOUT)
    );
    setCountdownIdentifier(countdownIdentifier + 1);
  };

  return (
    <div className="font-body text-theme-text-primary">
      <SVGSprite />
      <div className="flex flex-col min-h-screen">
        {csrUser?.userId && location.pathname.split("/")[1] !== "" && (
          <CsrSearchboard portal="agent" />
        )}
        {user?.userId ? <Header /> : !csrUser?.userId ? <UnauthHeader /> : ""}

        <main id="mainContent" className="flex flex-col flex-grow">
          <div
            className={`border border-transparent ${
              config.COMPANY_NAME === "nttdata" &&
              window.location.pathname === "/"
                ? "flex flex-grow"
                : ""
            }`}
          >
            {toastMessage && (
              <Toast
                title={toasTitle ?? t("Toast-title")}
                message={toastMessage}
                isSticky={true}
              />
            )}
            {config.COMPANY_NAME === "nttdata" &&
            window.location.pathname === "/" ? (
              <></>
            ) : (
              <AgentBreadcrumb />
            )}
            {user?.userId ? (
              <AuthenticatedUserChat
                hideCustomChat={hideCustomChat}
                setHideCustomChat={setHideCustomChat}
                connectingToSF={connectingToSF}
                setConnectingToSF={setConnectingToSF}
                openChatWindow={openChatWindow}
                setOpenChatWindow={setOpenChatWindow}
                policies={agentPolicies}
              />
            ) : (
              <UnauthenticatedUserChat
                setHideCustomChat={setHideCustomChat}
                openChatWindow={openChatWindow}
                setOpenChatWindow={setOpenChatWindow}
                connectingToSF={connectingToSF}
                setConnectingToSF={setConnectingToSF}
              />
            )}

            <Routes>
              <Route
                path="/"
                element={
                  <Landing
                    portal="agent"
                    hideCustomChat={hideCustomChat}
                    setHideCustomChat={setHideCustomChat}
                    connectingToSF={connectingToSF}
                    setConnectingToSF={setConnectingToSF}
                    openChatWindow={openChatWindow}
                    setOpenChatWindow={setOpenChatWindow}
                    bannerMessage={bannerMessage}
                    bannerTitle={bannerTitle}
                  />
                }
              />
              <Route path="/register" element={<Register />} />
              <Route
                path="/forgot-password"
                element={<ForgotPasswordContainer />}
              />
              <Route
                path="/forgot-username"
                element={<ForgotUsernameContainer />}
              />
              <Route
                path="/reset-password"
                element={<ResetPasswordContainer />}
              />
              <Route path="/forms" element={<Forms />} />
              <Route path="/help/file-a-claim" element={<FileAClaim />} />
              {/* agent profile routes */}

              <Route
                path="/csradmin"
                element={isCSRAdmin(csrUser) ? <CSRAdmin /> : <AccessDenied />}
              />

              <Route
                path="/csr/forms"
                element={
                  isCSR(csrUser) || isCSRAdmin(csrUser) ? (
                    <CSRFormsPanel />
                  ) : (
                    <AccessDenied />
                  )
                }
              />

              {/* policy routes */}
              <Route
                path="/dashboard"
                element={
                  useAuthorizationCheck(13) ? (
                    <Dashboard />
                  ) : csrUser?.userId && !user?.userId ? (
                    <div />
                  ) : user?.role?.id !== 7 ? (
                    <AccessDenied />
                  ) : (
                    <Commissions />
                  )
                }
              />

              <Route
                path="/policies/:policy_id"
                element={
                  useAuthorizationCheck(16) ? (
                    <PolicyDetails />
                  ) : (
                    <AccessDenied />
                  )
                }
              />

              <Route
                path="/policyLookup"
                element={
                  useAuthorizationCheck(16) ? (
                    <PolicyLookUp />
                  ) : (
                    <AccessDenied />
                  )
                }
              />

              {/* Agent Make a Payment */}
              <Route
                path="/payment/:policy_id"
                element={
                  useAuthorizationCheck(22) ? (
                    <MakeAPayment />
                  ) : (
                    <AccessDenied />
                  )
                }
              />

              <Route
                path="/payment"
                element={
                  useAuthorizationCheck(22) ? (
                    <MakeAPayment />
                  ) : (
                    <AccessDenied />
                  )
                }
              />

              {/* Agent Book of business */}
              <Route
                path="/book-of-business"
                element={
                  useAuthorizationCheck(19) ? (
                    <BookOfBusiness />
                  ) : (
                    <AccessDenied />
                  )
                }
              />

              {/* Agent service and claim */}
              <Route
                path="/services-claims"
                element={
                  useAuthorizationCheck(14) ? (
                    <ServicesClaims />
                  ) : (
                    <AccessDenied />
                  )
                }
              />
              <Route
                path="/claims"
                element={
                  useAuthorizationCheck(14) ? (
                    config.COMPANY_NAME === "everlake" ? (
                      <ServicesClaims />
                    ) : (
                      <AgentClaims />
                    )
                  ) : (
                    <AccessDenied />
                  )
                }
              />
              <Route
                path="/services-claims/claims"
                element={
                  useAuthorizationCheck(17) ? <Claims /> : <AccessDenied />
                }
              />
              <Route
                path="/services-claims/claims/file-a-claim"
                element={
                  useAuthorizationCheck(17) ? <FileAClaim /> : <AccessDenied />
                }
              />
              <Route
                path="/services-claims/claims/:id"
                element={
                  useAuthorizationCheck(17) ? (
                    <ClaimsStatus
                      policies={agentPolicies}
                      claims={agentClaims}
                    />
                  ) : (
                    <AccessDenied />
                  )
                }
              />

              <Route
                path="/services"
                element={
                  useAuthorizationCheck(14) &&
                  isFlagEnabled("ENABLE_SERVICE_TRACKING") ? (
                    <Services />
                  ) : (
                    <AccessDenied />
                  )
                }
              />

              <Route
                path="/services-claims/forms"
                element={
                  useAuthorizationCheck(23) ? <Forms /> : <AccessDenied />
                }
              />

              {/* message routes */}
              <Route
                path="/messages"
                element={
                  useAuthorizationCheck(15) ? <Messages /> : <AccessDenied />
                }
              />

              {/* Agent commissions */}
              <Route
                path="/commissions"
                element={
                  useAuthorizationCheck(18) &&
                  (user?.role.id !== 4 ||
                    (user?.role.id === 4 && user?.viewCommissions === 1)) ? (
                    <Commissions />
                  ) : (
                    <AccessDenied />
                  )
                }
              />

              {/* help routes */}
              <Route
                path="/help"
                element={
                  <Help
                    openChatWindow={openChatWindow}
                    setOpenChatWindow={setOpenChatWindow}
                  />
                }
              />
              <Route path="/help/faqs" element={<HelpFaqs />} />

              {/* Agent profile */}
              <Route path="/profile" element={<Profile />} />

              <Route
                path="/profile/assistant-details"
                element={<AssistantDetails />}
              />

              {/* Agent Term of Uses */}
              <Route path="/terms-of-use" element={<TermsOfUse />} />
              <Route path="/privacy-policy" element={<PrivacyPolicy />} />
              <Route
                path="/california-consumer-privacy-statement"
                element={<CaliforniaConsumerPrivacyStatement />}
              />
            </Routes>
          </div>
        </main>
        <Footer portal="agent" />
      </div>
      {/* user session */}
      {(user?.userId || csrUser?.userId) && (
        <>
          <Countdown
            date={currentCounterTime}
            key={countdownIdentifier}
            onComplete={() => setSessionDialogState(true)}
            renderer={() => false}
          />
          <AgentSessionRefresherDialog
            modalState={sessionDialogState}
            setModalState={setSessionDialogState}
            extendSession={extendSession}
          />
        </>
      )}

      <GeneralPurposeDialog
        state={tokenErrorDialog}
        setState={setTokenErrorDialog}
        title="Session expired"
        showCloseButton={false}
        desc={
          user?.userId
            ? "Your session has expired, please click button below to login to the portal again"
            : "Your session has expired, please click button below to refresh the session and refresh the page if needed."
        }
        dialogOnClose={() => null}
        enablePrimaryButton={true}
        primaryButtonText={user?.userId ? "Back to log in" : "Refresh session"}
        primaryButtonAction={() => {
          setTokenErrorDialog(false);

          if (user?.userId) {
            navigate("/");
          }
        }}
        enableSecondaryButton={false}
      />
    </div>
  );
}
